import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { AnimatePresence, motion } from "framer-motion";
import { ExternalLink, Scan, Settings, Wand2 } from "lucide-react";
import { useRef } from "react";
import { z } from "zod";
import { PageLayout } from "~/components/page-layout";
import { Button } from "~/components/ui/button";
import { Separator } from "~/components/ui/separator";
import { Tabs, TabsList, TabsTrigger } from "~/components/ui/tabs";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "~/components/ui/tooltip";
import { useHandleStudioAuth } from "~/hooks/use-handle-studio-auth";
import { isFeatureFlagEnabled } from "~/instrumentation/feature-flags";
import { trpc } from "~/lib/trpc";
import { cn } from "~/lib/utils";
import { DeleteVariantButton } from "~/routes/(app)/pages/$pageId/variants/$variantId/-components/delete-variant-button";
import { EditableVariantName } from "~/routes/(app)/pages/$pageId/variants/$variantId/-components/editable-variant-name";
import { VariantChat } from "~/routes/(app)/pages/$pageId/variants/$variantId/-components/variant-chat";
import {
  VariantPreview,
  type VariantPreviewRef,
} from "~/routes/(app)/pages/$pageId/variants/$variantId/-components/variant-preview";
import { useTabsState } from "~/routes/(app)/pages/$pageId/variants/$variantId/-hooks/use-tabs-state";
import { useVariantContent } from "~/routes/(app)/pages/$pageId/variants/$variantId/-hooks/use-variant-content";
import { useVariantSocket } from "~/routes/(app)/pages/$pageId/variants/$variantId/-hooks/use-variant-socket";
import { VariantContentStore } from "~/routes/(app)/pages/$pageId/variants/$variantId/-hooks/variant-content-store";
import { SaveBar } from "./-components/save-bar";
import { TrafficSourcesTable } from "./-components/traffic-sources-table";

export const Route = createFileRoute(
  "/(app)/pages/$pageId/variants/$variantId/",
)({
  component: RouteComponent,
  validateSearch: (search) => {
    const tab = z.enum(["editor", "settings"]).safeParse(search.tab);

    return {
      tab: tab.success ? tab.data : "editor",
    };
  },

  loader: async ({ context, params }) => {
    const [variantPageData, _messages, _trafficRules, _suggestions] =
      await Promise.all([
        context.queryClient.ensureQueryData(
          trpc.variants.getVariantPageData.queryOptions({
            variantId: params.variantId,
            pageId: params.pageId,
          }),
        ),
        context.queryClient.ensureQueryData(
          trpc.variantChat.getMessages.queryOptions({
            variantId: params.variantId,
          }),
        ),
        context.queryClient.ensureQueryData(
          trpc.variantTrafficSources.list.queryOptions({
            variantId: params.variantId,
          }),
        ),
        context.queryClient.ensureQueryData(
          trpc.variantChat.getSuggestions.queryOptions({
            pageId: params.pageId,
          }),
        ),
      ]);

    const variantContentStore = await VariantContentStore.init(
      params.variantId,
      variantPageData.elements,
    );

    return {
      variantContentStore,
      initialElements: await variantContentStore.getElements(params.variantId),
      crumb: {
        label: (
          <EditableVariantName
            variantId={params.variantId}
            pageId={params.pageId}
          />
        ),
        options: [],
        id: "variant-name",
      },
    };
  },
});

function RouteComponent() {
  const params = Route.useParams();
  const variantId = params.variantId;
  const previewRef = useRef<VariantPreviewRef>(null);

  const { data } = useSuspenseQuery(
    trpc.variants.getVariantPageData.queryOptions({
      variantId,
      pageId: params.pageId,
    }),
  );

  const { data: suggestionsData } = useSuspenseQuery(
    trpc.variantChat.getSuggestions.queryOptions({
      pageId: params.pageId,
    }),
  );

  const { variantContentStore, initialElements } = Route.useLoaderData();
  const { elements, discardAll, hasChanges, changedElementsCount, save } =
    useVariantContent({
      initialElements,
      variantId,
      variantContentService: variantContentStore,
      onDiscard: (content) => {
        previewRef.current?.onContentDiscard(
          content.map((element) => ({
            elementId: element.elementId,
            selector: element.selector,
            savedContent: element.savedPersonalisedContent,
          })),
        );
      },
      onSave: (content) => {
        previewRef.current?.onContentSave(
          content.map((element) => ({
            elementId: element.elementId,
            selector: element.selector,
            savedContent: element.savedPersonalisedContent,
          })),
        );
      },
    });

  useVariantSocket({
    variantId,
    pageId: params.pageId,
    variantContentStore,
    onContentUpdate: (updates) => {
      previewRef.current?.onContentUpdate(updates);
    },
  });

  const { activeTab, setActiveTab } = useTabsState();

  const { open } = useHandleStudioAuth();

  return (
    <>
      <PageLayout.Header className="flex items-center justify-end w-full">
        <div className="flex w-full justify-end items-center gap-2">
          {activeTab === "editor" && (
            <AnimatePresence initial={false}>
              {hasChanges && (
                <motion.div
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  className="flex items-center justify-end"
                >
                  <SaveBar
                    changedElementsCount={changedElementsCount}
                    discardAll={discardAll}
                    save={save}
                  />

                  <div className="flex items-center justify-center ml-3">
                    <div className="w-px h-5 bg-border" />
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          )}
        </div>

        <div className="flex h-full items-center justify-end">
          <Tabs
            value={activeTab}
            onValueChange={(value) =>
              setActiveTab(value as "editor" | "settings")
            }
            className="bg-transparent m-0"
          >
            <TabsList className="ml-2 bg-transparent border-none shadow-none flex gap-1">
              <TabsTrigger
                value="editor"
                className={cn(
                  "data-[state=active]:bg-muted data-[state=active]:shadow-none max-w-fit border border-border px-2.5 h-9",
                  activeTab === "editor" ? "bg-muted" : "bg-transparent",
                )}
              >
                <Wand2 className="text-muted-foreground !h-[14px]" />
                Editor
              </TabsTrigger>
              <Tooltip>
                <TooltipTrigger asChild>
                  <TabsTrigger
                    value="settings"
                    className={cn(
                      "data-[state=active]:shadow-none max-w-fit border border-border !w-9 h-9 shrink-0",
                      activeTab === "settings" ? "bg-muted" : "bg-transparent",
                    )}
                  >
                    <Settings className="text-muted-foreground !h-[14px]" />
                  </TabsTrigger>
                </TooltipTrigger>
                <TooltipContent>Settings</TooltipContent>
              </Tooltip>

              <Separator orientation="vertical" className="!h-5 mx-2" />

              <Tooltip>
                <TooltipTrigger>
                  <Button
                    variant="outline"
                    size="sm"
                    asChild
                    className="max-w-fit border border-border !w-9 h-9 shrink-0"
                  >
                    <a href={data.variant.previewUrl} target="_blank">
                      <ExternalLink />
                    </a>
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Preview</TooltipContent>
              </Tooltip>
              <DeleteVariantButton
                variantId={variantId}
                pageId={params.pageId}
              />

              {isFeatureFlagEnabled("studio") && (
                <>
                  <Separator className="!h-5 mx-2" orientation="vertical" />
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() =>
                          open(getStudioUrl(data.variant.previewUrl, variantId))
                        }
                      >
                        <Scan />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Studio</TooltipContent>
                  </Tooltip>
                </>
              )}
            </TabsList>
          </Tabs>
        </div>
      </PageLayout.Header>

      <PageLayout.Main className="w-full h-full relative">
        {activeTab === "editor" && (
          <div className="flex gap-3 h-full w-full pb-4">
            <VariantPreview
              ref={previewRef}
              snapshot={data.snapshot}
              elements={elements}
              variantContentStore={variantContentStore}
              variantId={variantId}
            />
            <VariantChat
              pageId={params.pageId}
              variantId={variantId}
              elements={elements}
              suggestions={suggestionsData.suggestions}
            />
          </div>
        )}

        {activeTab === "settings" && (
          <VariantSettingsPage variantId={variantId} />
        )}
      </PageLayout.Main>
    </>
  );
}

export function VariantSettingsPage({
  variantId,
}: {
  variantId: string;
}) {
  const { data } = useSuspenseQuery(
    trpc.variantTrafficSources.list.queryOptions({
      variantId,
    }),
  );

  return (
    <div className="mx-auto w-full max-w-[1000px] space-y-8">
      <TrafficSourcesTable
        tableData={{
          rows: data.rules.map((rule) => ({
            id: rule.id,
            name: rule.name,
            type: rule.type,
            rule: rule.rule,
            testLink: rule.testLink,
          })),
          total: data.rules.length,
          page: 1,
          pageSize: data.rules.length,
        }}
      />
    </div>
  );
}

// TODO get URL from the page, not by modifying the preview URL
function getStudioUrl(previewUrl: string, variantId: string) {
  const url = new URL(previewUrl);
  url.searchParams.delete("variant-preview");
  url.searchParams.set("variant", variantId);
  return url.toString();
}
